import React, { useEffect, useState } from "react";
import AdminLayout from "../../Layout";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { useParams } from "react-router-dom";
import axios from "axios";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Button, Select } from "flowbite-react";
import Loader from "../../../../components/Loader";
import * as XLSX from "xlsx";
import { AiOutlineDownload } from "react-icons/ai";

const CampusFeedback = () => {
  const { college_name } = useParams();
  const [feedbackData, setFeedbackData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("All");
  const [batchCounts, setBatchCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [Isload, setIsload] = useState(true);

  useEffect(() => {
    setTimeout(() => {
      setIsload(false);
    }, 2000);
  }, []);

  // ✅ GROUP batch_* INTO SINGLE BATCH FIELD
  const transformBatchFields = (data) => {
    return data.map((item) => {
      const batchValues = [];

      Object.entries(item).forEach(([key, value]) => {
        if (key.startsWith("batch_") && value) {
          batchValues.push(value);
        }
      });

      const cleaned = { ...item };
      Object.keys(cleaned).forEach((key) => {
        if (key.startsWith("batch_")) {
          delete cleaned[key];
        }
      });

      return {
        ...cleaned,
        BATCH: batchValues.join(", "),
      };
    });
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/apiforms/api/getFeedbackByCollege`,
          { params: { collegeName: college_name } }
        );

        if (response.data.success) {
          const transformedData = transformBatchFields(response.data.data);


          setFeedbackData(transformedData);
          setFilteredData(transformedData);

          const batchCounts = {};
          const batchSet = new Set();

          transformedData.forEach((feedback) => {
            if (feedback.BATCH) {
              feedback.BATCH.split(",").forEach((b) => {
                const batch = b.trim();
                batchSet.add(batch);
                batchCounts[batch] = (batchCounts[batch] || 0) + 1;
              });
            }
          });

          batchCounts["All"] = transformedData.length;
          setBatches(["All", ...Array.from(batchSet)]);
          setBatchCounts(batchCounts);
        }
      } catch (error) {
        console.error("Error fetching feedback data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [college_name]);

  useEffect(() => {
    applyFilters();
  }, [selectedBatch, selectedDate, selectedFilter, fromDate, toDate, searchTerm]);

  const applyFilters = () => {
    let data = [...feedbackData];

    if (selectedBatch !== "All") {
      data = data.filter((feedback) =>
        feedback.BATCH?.split(",").map((b) => b.trim()).includes(selectedBatch)
      );
    }

    if (selectedFilter === "SpecificDate" && selectedDate) {
      const selected = new Date(selectedDate).toISOString().split("T")[0];
      data = data.filter((feedback) => {
        const date = new Date(feedback.timestamp).toISOString().split("T")[0];
        return date === selected;
      });
    }

    if (selectedFilter === "Custom" && fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      data = data.filter((feedback) => {
        const feedbackDate = new Date(feedback.timestamp);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }

    if (["Today", "Yesterday", "DayBeforeYesterday"].includes(selectedFilter)) {
      const currentDate = new Date();
      let targetDate;

      if (selectedFilter === "Today") targetDate = currentDate;
      else if (selectedFilter === "Yesterday")
        targetDate = new Date(currentDate.setDate(currentDate.getDate() - 1));
      else if (selectedFilter === "DayBeforeYesterday")
        targetDate = new Date(currentDate.setDate(currentDate.getDate() - 2));

      const target = targetDate.toISOString().split("T")[0];
      data = data.filter(
        (feedback) =>
          new Date(feedback.timestamp).toISOString().split("T")[0] === target
      );
    }

    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      data = data.filter((feedback) =>
        Object.values(feedback).some(
          (val) => typeof val === "string" && val.toLowerCase().includes(lower)
        )
      );
    }

    setFilteredData(data);
  };

  const handleDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Feedback");
    XLSX.writeFile(workbook, `${college_name}_feedback.xlsx`);
  };

  if (loading || Isload) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  const excludedColumns = ["college_name", "_id"];
  const filteredColumns = Object.keys(filteredData[0] || {}).filter(
    (key) =>
      !excludedColumns.includes(key) &&
      !key.startsWith("batch_") &&
      filteredData.some((feedback) => feedback[key] && feedback[key] !== "")
  );

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-4 space-y-6 sm:p-6 md:p-10 sm:space-y-8">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
          >
            <IoMdArrowRoundBack className="w-6 h-6" />
            <span className="text-lg font-semibold sm:text-xl">Back</span>
          </button>

          <h2 className="text-2xl font-bold text-center text-gray-800 sm:text-3xl sm:text-left">
            c {college_name}
          </h2>

          <div className="text-sm sm:text-right">
            <div className="font-medium text-gray-700">
              Total Feedback:{" "}
              <span className="text-black">{batchCounts["All"] || 0}</span>
            </div>
            <div className="font-medium text-gray-700">
              Feedback in {selectedBatch}:{" "}
              <span className="text-black">
                {selectedBatch === "All"
                  ? filteredData.length
                  : filteredData.filter((feedback) =>
                      feedback.BATCH?.includes(selectedBatch)
                    ).length}
              </span>
            </div>
          </div>
        </div>

        {/* ✅ FILTERS UI UNCHANGED */}
        <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:gap-6">
          <div className="flex flex-col w-full md:w-[12rem]">
            <label className="mb-1 text-sm font-semibold">Filter by Batch</label>
            <Select value={selectedBatch} onChange={(e) => setSelectedBatch(e.target.value)}>
              {batches.map((batch) => (
                <option key={batch} value={batch}>
                  {batch} ({batchCounts[batch] || 0})
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-col w-full md:w-[28rem]">
            <label className="mb-1 text-sm font-semibold">Filter by Feedback Type</label>
            <div className="flex gap-2">
              <Select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="w-1/2"
              >
                <option value="All">All</option>
                <option value="Today">Today</option>
                <option value="Yesterday">Last Day</option>
                <option value="DayBeforeYesterday">Last Last Day</option>
                <option value="SpecificDate">Select Date</option>
                <option value="Custom">Custom Range</option>
              </Select>

              {selectedFilter === "SpecificDate" && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-1/2 px-3 py-2 border border-gray-300 rounded-md"
                />
              )}
            </div>
          </div>

          {selectedFilter === "Custom" && (
            <div className="flex flex-col w-full md:w-[20rem]">
              <div className="flex gap-4">
                <div className="flex flex-col w-1/2">
                  <label className="mb-1 text-sm font-semibold text-gray-700">From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-2 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex flex-col w-1/2">
                  <label className="mb-1 text-sm font-semibold text-gray-700">To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col w-full md:w-[8rem] justify-end">
            <label className="invisible mb-1 text-sm font-semibold">Download</label>
            <Button onClick={handleDownload} className="flex items-center justify-center gap-2">
              <AiOutlineDownload className="w-5 h-5" />
              Download
            </Button>
          </div>
        </div>

        {/* ✅ TABLE */}
        <div className="overflow-x-auto bg-white rounded-lg shadow-md">
          <table className="min-w-full text-sm border table-fixed">
            <thead className="text-gray-700 bg-gray-100">
              <tr>
                {filteredColumns.map((key) => (
                  <th key={key} className="px-4 py-2 text-left border whitespace-nowrap">
                    {key.replace(/_/g, " ").toUpperCase()}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={filteredColumns.length} className="px-4 py-4 text-center text-gray-500">
                    No feedback available for this college.
                  </td>
                </tr>
              ) : (
                filteredData.map((feedback, index) => (
                  <tr
                    key={index}
                    className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-blue-50 transition`}
                  >
                    {filteredColumns.map((key) => (
                      <td
                        key={key}
                        className="px-4 py-2 border align-top break-words max-w-[200px] whitespace-normal"
                      >
                        {key === "timestamp"
                          ? new Date(feedback[key]).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : feedback[key] || "N/A"}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CampusFeedback;